import { useRef, useState } from "react";
import type { DragEvent } from "react";
import { FileText, Upload, X } from "./icons";

/**
 * ช่องเลือกไฟล์แบบลากวางได้ — แทน `<input type="file">` ดิบที่ขึ้น "Choose File / No file chosen"
 * เป็นภาษาของเบราว์เซอร์ (ผู้ใช้อ่านไม่ออกว่าต้องทำอะไร) · input จริงซ่อนไว้ข้างใน คีย์บอร์ดยังใช้ได้
 *
 * ไม่ตรวจชนิด/ขนาดเอง — ผู้เรียกตรวจใน onPick แล้วบอก error ของตัวเอง
 */
export function FileDrop({
  accept,
  file,
  hint,
  disabled = false,
  onPick,
}: {
  accept: string;
  file: File | null;
  /** บรรทัดเล็กใต้ปุ่ม เช่น "PDF ຫຼື ຮູບ · ບໍ່ເກີນ 10 MB" */
  hint: string;
  disabled?: boolean;
  onPick: (f: File | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function drop(e: DragEvent<HTMLElement>) {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    const f = e.dataTransfer.files[0];
    if (f) onPick(f);
  }

  const hidden = (
    <input
      ref={input}
      type="file"
      accept={accept}
      hidden
      onChange={(e) => {
        onPick(e.target.files?.[0] ?? null);
        // เลือกไฟล์เดิมซ้ำได้ (เช่น หลังลบออกแล้วเลือกใหม่)
        e.target.value = "";
      }}
    />
  );

  if (file) {
    return (
      <div className="file-drop__chosen">
        {hidden}
        <span className="file-drop__icon" aria-hidden="true">
          <FileText size={18} />
        </span>
        <span className="file-drop__meta">
          <span className="file-drop__name" title={file.name}>
            {file.name}
          </span>
          <span className="caption num">{formatSize(file.size)}</span>
        </span>
        <button
          type="button"
          className="btn-outline btn-sm"
          disabled={disabled}
          onClick={() => input.current?.click()}
        >
          ປ່ຽນໄຟລ໌
        </button>
        <button
          type="button"
          className="file-drop__clear"
          disabled={disabled}
          aria-label="ເອົາໄຟລ໌ອອກ"
          onClick={() => onPick(null)}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={over ? "file-drop is-over" : "file-drop"}
      disabled={disabled}
      onClick={() => input.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      {hidden}
      <span className="file-drop__icon" aria-hidden="true">
        <Upload size={20} />
      </span>
      <span className="file-drop__title">ເລືອກໄຟລ໌ ຫຼື ລາກມາວາງບ່ອນນີ້</span>
      <span className="caption">{hint}</span>
    </button>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString("lo-LA")} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString("lo-LA", { maximumFractionDigits: 1 })} MB`;
}
